import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Sun, Moon } from 'lucide-react';
import { LANDING } from 'constants/testIds';
import useTheme from 'hooks/useTheme';
import { useLegal } from 'components/app/LegalModal';

const NAV_LINKS = [
  { label: 'About us', key: 'about', testId: 'landing-nav-about' },
  { label: 'Community', scrollTo: 'community', testId: 'landing-nav-community' },
  { label: 'Contact us', key: 'contact', testId: 'landing-nav-contact' },
];

export const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();
  const legal = useLegal();
  const logoSrc = theme === 'light' ? '/qreativs-logo.png' : '/qreativs-logo-dark.png';

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleNavClick = (link) => {
    if (link.scrollTo) {
      const el = document.getElementById(link.scrollTo);
      if (el) {
        const headerOffset = 80;
        const top = el.getBoundingClientRect().top + window.scrollY - headerOffset;
        window.scrollTo({ top, behavior: 'smooth' });
      }
      return;
    }
    if (link.key) legal?.openPage(link.key);
  };

  return (
    <motion.header
      initial={{ y: -20, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-300 ${
        scrolled
          ? 'bg-zinc-950/70 backdrop-blur-xl border-b border-zinc-800/80'
          : 'bg-transparent border-b border-transparent'
      }`}
    >
      <nav className="mx-auto max-w-7xl px-5 sm:px-6 lg:px-10 h-16 md:h-[4.5rem] grid grid-cols-[1fr_auto] md:grid-cols-[1fr_auto_1fr] items-center gap-3">
        {/* Left — brand */}
        <div className="justify-self-start flex items-center min-w-0">
          <a
            href="#top"
            data-testid={LANDING.navWordmark}
            className="inline-flex items-center transition-opacity hover:opacity-90"
            aria-label="qreativs"
          >
            <img
              src={logoSrc}
              alt="qreativs"
              className="h-6 md:h-7 w-auto select-none"
              draggable={false}
            />
          </a>
        </div>

        {/* Center — true middle column */}
        <div className="justify-self-center hidden md:flex items-center">
          <div className="landing-nav-center flex items-center gap-0.5 rounded-full border border-zinc-800/70 bg-zinc-950/40 backdrop-blur-md px-1 py-1">
            {NAV_LINKS.map((l) => (
              <button
                key={l.label}
                type="button"
                onClick={() => handleNavClick(l)}
                data-testid={l.testId}
                className="text-[13px] tracking-wide text-zinc-400 hover:text-zinc-50 transition-colors font-medium rounded-full px-3.5 py-2 hover:bg-zinc-800/60 whitespace-nowrap"
              >
                {l.label}
              </button>
            ))}
          </div>
        </div>

        {/* Right — actions */}
        <div className="justify-self-end flex items-center gap-2">
          <div className="hidden md:flex items-center gap-2">
            <button
              data-testid="landing-nav-theme-toggle"
              onClick={toggle}
              aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
              className="landing-btn landing-btn-icon"
            >
              {theme === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
            </button>
            <button
              data-testid={LANDING.navLoginBtn}
              onClick={() => navigate('/signin')}
              className="landing-btn landing-btn-ghost"
            >
              Sign in
            </button>
            <button
              data-testid={LANDING.navJoinBtn}
              onClick={() => navigate('/signup')}
              className="landing-btn landing-btn-primary landing-btn-sm"
            >
              Join Qreativs
            </button>
          </div>

          <div className="md:hidden flex items-center">
            <button
              data-testid="landing-nav-theme-toggle-mobile"
              onClick={toggle}
              aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
              className="landing-btn landing-btn-icon"
            >
              {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
            </button>
            <button
              data-testid={LANDING.navMobileMenuBtn}
              onClick={() => setMobileOpen((v) => !v)}
              className="landing-btn landing-btn-icon"
              aria-label="Toggle menu"
            >
              {mobileOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>
      </nav>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="md:hidden overflow-hidden border-t border-zinc-800/80 bg-zinc-950/95 backdrop-blur-xl"
          >
            <div className="px-6 py-6 flex flex-col gap-1">
              {NAV_LINKS.map((l) => (
                <button
                  key={l.label}
                  type="button"
                  onClick={() => {
                    setMobileOpen(false);
                    handleNavClick(l);
                  }}
                  data-testid={`${l.testId}-mobile`}
                  className="text-base text-zinc-300 hover:text-zinc-50 transition-colors py-3 text-left rounded-xl px-2 hover:bg-zinc-900/80"
                >
                  {l.label}
                </button>
              ))}
              <div className="pt-4 flex flex-col gap-3">
                <button
                  onClick={() => { setMobileOpen(false); navigate('/signin'); }}
                  className="landing-btn landing-btn-secondary w-full"
                >
                  Sign in
                </button>
                <button
                  onClick={() => { setMobileOpen(false); navigate('/signup'); }}
                  className="landing-btn landing-btn-primary w-full"
                >
                  Join Qreativs
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export default Nav;
